"use client";
import { clsx } from "@/helpers/clsx";
import { useInView } from "framer-motion";
import React from "react";
import { FeatureContainer } from "./BentoBoxComponents";
import { LogoIcon } from "./LogoIcon";

export const RealtimeData = ({ children, logos }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(ref);
  return (
    <FeatureContainer>
      {children}
      <div
        className={clsx(
          "relative mt-8 flex h-[300px] items-center justify-center",
          !isInView && "!invisible",
        )}
        ref={ref}
      >
        <LogoIcon className="relative z-10 h-20 w-20 animate-pulse object-contain animate-infinite animate-duration-[2s]" />
        <div className="circles flex items-center justify-center">
          <Lines />
        </div>
        {isInView && <Circles logos={logos} />}
      </div>
    </FeatureContainer>
  );
};

const Lines = () => {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="circle"
          style={
            {
              width: 160 + i * 45,
              transform: `rotate(${i * -25}deg)`,
              opacity: 1 - i * 0.05,
              //"--color": i % 2 === 0 ? "100, 100, 100" : "50, 50, 50",
            } as React.CSSProperties
          }
        />
      ))}
    </>
  );
};

const Circles = ({ logos }) => {
  const [logosState, setLogosState] = React.useState(logos);

  const numElements = 6;
  const radius = 125;

  const getAngle = (index) => {
    return (index * 360) / numElements;
  };

  return (
    <div
      className="absolute inset-0 flex animate-rotate items-center justify-center animate-reverse animate-duration-[20s]"
      onMouseEnter={(e) => {
        const target = e.currentTarget as HTMLDivElement;
        for (const child of Array.from(target.children)) {
          child.classList.add("animate-pulse");
        }
      }}
    >
      {Array.from({ length: numElements }).map((_, index) => {
        const angle = getAngle(index);

        const x = radius * Math.cos((angle * Math.PI) / 180);
        const y = radius * Math.sin((angle * Math.PI) / 180);

        const logo = logosState[index];
        const width = (52 * random(7, 10)) / 10;

        return (
          <div
            key={index}
            className="absolute box-border flex origin-center animate-pulse items-center justify-center rounded-full border border-white/10 bg-black/50 text-xs shadow-md shadow-black/30 ring-[2px] ring-[#111111] animate-duration-[5s]"
            style={{
              transform: `translate(${x}px, ${y}px)`,
              width,
              height: width,
              background: logo.color,
            }}
            onAnimationStart={(e) => {
              if (index !== 0) return;
              setTimeout(() => {
                setLogosState(shuffleArray(logos));
              }, 2500);
            }}
            onAnimationEnd={(e) => {
              const target = e.target as HTMLDivElement;
              target.classList.remove("animate-pulse");

              setTimeout(() => {
                if (!target) return;
                target.classList.add("animate-pulse");
              }, 5000);
            }}
          >
            <svg
              className={clsx(
                "absolute inset-0 mx-auto my-auto max-h-[36px] animate-rotate opacity-80 brightness-0 invert transition-none duration-[20s] animate-duration-[20s]",
                logo.className,
              )}
              style={{
                width: width * 0.6,
                height: width * 0.6,
              }}
              aria-label={`${logo.id} logo`}
            >
              <use href={`/_static/sprites/logos.svg#${logo.id}`} />
            </svg>
          </div>
        );
      })}
    </div>
  );
};

function random(min, max) {
  // min and max included
  return Math.floor(Math.random() * (max - min + 1) + min);
}

const shuffleArray = (input) => {
  const array = [...input];
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }

  return array;
};
